import { type ReactElement, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AIAttachmentUploader from '../../../../../components/AIAttachmentUploader';
import AttachmentBulkUploadModal from '../AttachmentBulkUploadModal';

import { aiAttachmentUploaderMaxCount, attachmentSupportedFileTypes } from '../../../constants';
import useLicenseAbbreviation from '../../../../../hooks/useLicenseAbbreviation';
import { type IAttachedDocumentResponse } from '../../../types/response';
import { AttachedDocumentsTabFields } from '../../../types/fieldEnums';
import { type FileType } from '../../../../../constants';
import { type IAttachmentFormValues } from '../types';

import { attachedDocExtensionsSelector, attachedDocMaxSizeSelector } from '../../../../../store/configs/selectors';
import getSelectValue from '../../../helpers/getSelectValue';

interface IUploadAttachmentsButtonProps {
  onUploadAttachments: (data: IAttachmentFormValues[]) => void;
  button?: ReactElement | null;
  isMultiple?: boolean;
  showButtonTooltip?: boolean;
  errorMessage?: string;
  attachmentCurrentValues?: IAttachmentFormValues;
}

function UploadAttachmentsButton({
  onUploadAttachments,
  errorMessage,
  isMultiple,
  showButtonTooltip,
  button = null,
  attachmentCurrentValues,
}: IUploadAttachmentsButtonProps) {
  const { t } = useTranslation();
  const licenseAbbreviation = useLicenseAbbreviation();

  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState<string[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<IAttachmentFormValues[]>([]);

  const allowedFileTypes = useSelector(attachedDocExtensionsSelector);
  const attachedDocMaxSize = useSelector(attachedDocMaxSizeSelector);

  useEffect(() => {
    setIsModalOpened(isUploading || invalidFiles.length > 0 || uploadedAttachments.length > 0);
  }, [invalidFiles.length, isUploading, uploadedAttachments.length]);

  const addAttachments = (data: IAttachmentFormValues[]) => {
    onUploadAttachments(data);
    setIsModalOpened(false);
  };

  const closeModal = () => {
    setInvalidFiles([]);
    setUploadedAttachments([]);
    setIsUploading(false);
  };

  const uploadCallback = (data: IAttachedDocumentResponse[]) => {
    if (attachmentCurrentValues && data[0]) {
      const { fileUrl = '', fileName = '' } = data[0];
      setUploadedAttachments([
        {
          ...attachmentCurrentValues,
          fileUrl,
          fileName,
        },
      ]);
    } else {
      setUploadedAttachments(
        data.map(
          ({ name, ...attachedDocument }) =>
            ({
              ...attachedDocument,
              [AttachedDocumentsTabFields.code]: getSelectValue({
                value: attachedDocument.code,
                tagLabel: name,
              }),
            } as IAttachedDocumentResponse<SelectOption>)
        )
      );
    }
  };

  return (
    <>
      <AIAttachmentUploader
        labelHelpText={t('Use this field to attach documents to the {{licenseAbbreviation}} application.', {
          licenseAbbreviation,
        })}
        supportedFileTypes={(allowedFileTypes as FileType[]) || attachmentSupportedFileTypes}
        supportedMaxFileSizeInMB={attachedDocMaxSize}
        maxCount={aiAttachmentUploaderMaxCount}
        showButtonTooltip={showButtonTooltip}
        setInvalidFiles={setInvalidFiles}
        setIsUploading={setIsUploading}
        uploadCallback={uploadCallback}
        errorMessage={errorMessage}
        isUploading={isUploading}
        isMultiple={isMultiple}
        button={button}
      />
      {isModalOpened && (
        <AttachmentBulkUploadModal
          uploadedAttachments={uploadedAttachments}
          invalidFiles={invalidFiles}
          onConfirm={addAttachments}
          isUploading={isUploading}
          title="Document Upload"
          onCancel={closeModal}
        />
      )}
    </>
  );
}

export default UploadAttachmentsButton;
